<template>
  <Dialog
    :visible="visible"
    :style="{ width: '450px' }"
    header="Nueva solicitud de vacaciones"
    :modal="true"
    class="p-fluid"
    @update:visible="emit('update:visible', $event)"
  >
    <div class="field flex justify-content-left" v-if="allowPayedVacations">
      <label for="dataInit" class="font-medium text-700 mr-2"
        >Vacaciones en dinero:</label
      >
      <InputSwitch
        :model-value="vacationModel.isPaid ?? false"
        class="h-2"
        @update:model-value="onFieldUpdate('isPaid', $event)"
      />
    </div>
    <div class="field">
      <label for="dataInit" class="font-medium text-700"
        >Fecha inicial <span class="text-red-500">*</span></label
      >
      <Calendar
        id="dataInit"
        :model-value="vacationModel.dateInit"
        required="true"
        :class="{ 'p-invalid': submitted && !vacationModel.dateInit }"
        :disabledDays="disabledDays"
        :disabled-dates="disabledDates"
        date-format="dd/mm/yy"
        @update:model-value="onDateInitUpdate"
        @blur="emit('calculate-date-end')"
      >
        <template #date="{ date }">
          <div class="flex flex-column">
            <span>{{ date.day }}</span>
            <span :class="badgeResolver?.(date) ?? ''"></span>
          </div>
        </template>
        <template #footer>
          <div class="ml-6 mt-3">
            <div class="flex align-items-center">
              <div class="mr-2 vacation-approved"></div>
              <label class="text-sm font-semibold text-600 mt-2"
                >Vacaciones aprobadas</label
              >
            </div>
            <div class="flex align-items-center">
              <div class="mr-2 vacation-pending"></div>
              <label class="text-sm font-semibold text-600 mt-2"
                >Vacaciones pendientes</label
              >
            </div>
            <div class="flex align-items-center">
              <div class="mr-2 license-approved"></div>
              <label class="text-sm font-semibold text-600 mt-2"
                >Licencias aprobadas</label
              >
            </div>
            <div class="flex align-items-center">
              <div class="mr-2 license-pending"></div>
              <label class="text-sm font-semibold text-600 mt-2"
                >Licencias pendientes</label
              >
            </div>
            <div class="flex align-items-center">
              <div class="mr-2 holiday"></div>
              <label class="text-sm font-semibold text-600 mt-2"
                >Feriados</label
              >
            </div>
          </div>
        </template>
      </Calendar>
      <small class="p-error" v-if="submitted && !vacationModel.dateInit"
        >La fecha inicial es requerida.</small
      >
    </div>
    <div class="field">
      <label for="cantDays" class="font-medium text-700 flex flex-row">
        Cantidad de dias ({{ vacationBalance - pendingBalance }} disponibles)
        <span class="text-red-500">*</span>
        <InfoButton
          :message="`Saldo acumulado: ${vacationBalance} días. Solicitudes pendientes por aprobar: ${pendingBalance} días. Días disponibles para nuevas solicitudes: ${
            vacationBalance - pendingBalance
          } días.`"
        />
      </label>
      <InputNumber
        id="cantDays"
        :model-value="vacationModel.quantityDays"
        :class="{ 'p-invalid': submitted && !vacationModel.quantityDays }"
        @update:model-value="onFieldUpdate('quantityDays', $event)"
        :max="
          !allowNegativePTOBalance
            ? vacationBalance - pendingBalance
            : undefined
        "
        :min="0"
        @blur="emit('calculate-date-end')"
      />
      <small class="p-error" v-if="submitted && !vacationModel.quantityDays"
        >Cantidad de dias requerido.</small
      >
    </div>
    <div class="field">
      <label for="dateEnd" class="font-medium text-700"
        >Fecha final <span class="text-red-500">*</span></label
      >
      <Calendar
        id="dateEnd"
        :model-value="vacationModel.dateEnd"
        :class="{ 'p-invalid': submitted && !vacationModel.dateEnd }"
        disabled
        date-format="dd/mm/yy"
      />
      <small class="p-error" v-if="submitted && !vacationModel.dateEnd"
        >La fecha final es requerida.</small
      >
    </div>
    <div class="field">
      <label for="adjunto" class="font-medium text-700"
        >Adjuntar documento</label
      >
      <FileUpload
        mode="basic"
        class="font-light text-sm w-auto text-center"
        style="font-size: 12px !important"
        upload-icon="pi pi-paperclip"
        custom-upload
        @uploader="emit('remove-file')"
        @select="emit('select-file', $event)"
      />
    </div>
    <div class="field">
      <label for="observation" class="font-medium text-700"
        >Observación <span class="text-red-500">*</span></label
      >
      <Textarea
        id="observation"
        :model-value="vacationModel.observation"
        required="true"
        :class="{ 'p-invalid': submitted && !vacationModel.observation }"
        @update:model-value="onFieldUpdate('observation', $event)"
      />
      <small class="p-error" v-if="submitted && !vacationModel.observation"
        >La observación es requerida.</small
      >
    </div>
    <template #footer>
      <Button
        label="Cancelar"
        icon="pi pi-times"
        severity="secondary"
        @click="emit('cancel')"
        class="text-sm font-light mr-2"
      />
      <Button
        label="Solicitar"
        icon="pi pi-check"
        :disabled="!isValidToSave"
        @click="emit('save-request')"
        class="text-sm font-light mr-2"
      />
    </template>
  </Dialog>
  <ConfirmDialog class="md:w-7 w-6" group="negativeBalanceCF"></ConfirmDialog>
</template>

<script setup lang="ts">
  import { computed, watch } from 'vue'
  import Dialog from 'primevue/dialog'
  import Calendar from 'primevue/calendar'
  import InputNumber from 'primevue/inputnumber'
  import InputSwitch from 'primevue/inputswitch'
  import Button from 'primevue/button'
  import FileUpload from 'primevue/fileupload'
  import Textarea from 'primevue/textarea'
  import InfoButton from '@/shared/components/InfoButton.vue'
  import type CreateVacationInputDto from '@/modules/employees/services/interfaces/CreateVacationInputDto'

  const props = defineProps<{
    visible: boolean
    vacation: CreateVacationInputDto
    allowPayedVacations: boolean
    allowNegativePTOBalance: boolean
    vacationBalance: number
    pendingBalance: number
    submitted: boolean
    disabledDays: number[]
    disabledDates: Date[]
    badgeResolver?: (date: any) => string
    isValidToSave: boolean
  }>()

  const emit = defineEmits<{
    (event: 'update:visible', value: boolean): void
    (event: 'update:vacation', value: CreateVacationInputDto): void
    (event: 'save-request'): void
    (event: 'cancel'): void
    (event: 'select-file', value: any): void
    (event: 'remove-file'): void
    (event: 'calculate-date-end'): void
  }>()

  const vacationModel = computed(() => props.vacation)

  const onFieldUpdate = <K extends keyof CreateVacationInputDto>(
    field: K,
    value: CreateVacationInputDto[K]
  ) => {
    emit('update:vacation', {
      ...vacationModel.value,
      [field]: value as CreateVacationInputDto[K],
    })
  }

  watch(
    () => props.visible,
    (newVal) => {
      console.log('VacationRequestDialog visibility changed:', newVal)
      console.log('Current vacation model:', props)
    }
  )

  const onDateInitUpdate = (
    value: Date | Date[] | string | string[] | undefined
  ) => {
    const single = Array.isArray(value) ? value[0] : value
    const date =
      single instanceof Date
        ? single
        : typeof single === 'string'
        ? new Date(single)
        : undefined
    onFieldUpdate('dateInit', date as CreateVacationInputDto['dateInit'])
  }
</script>

<style scoped>
  .vacation-approved {
    background-color: #4caf50;
    border-radius: 50%;
    height: 10px;
    width: 10px;
    border: 1px solid #4caf50;
  }

  .vacation-pending {
    background-color: #ff9800;
    border-radius: 50%;
    height: 10px;
    width: 10px;
    border: 1px solid #ff9800;
  }

  .license-approved {
    background-color: rgb(228, 57, 57);
    border-radius: 50%;
    height: 10px;
    width: 10px;
    border: 1px solid rgb(228, 57, 57);
  }

  .license-pending {
    background-color: rgb(240, 243, 56);
    border-radius: 50%;
    height: 10px;
    width: 10px;
    border: 1px solid rgb(240, 243, 56);
  }

  .holiday {
    background-color: rgb(56, 56, 243);
    border-radius: 50%;
    height: 10px;
    width: 10px;
    border: 1px solid rgb(56, 56, 243);
  }
</style>
